import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { submitReport } from '../../api/reportAPI';
import './ReportForm.css';

// Helper functions to detect environment info
const getBrowserInfo = () => {
  const userAgent = navigator.userAgent;
  let browser = "Unknown";
  
  if (userAgent.includes("Firefox")) browser = "Firefox";
  else if (userAgent.includes("Chrome")) browser = "Chrome";
  else if (userAgent.includes("Safari")) browser = "Safari";
  else if (userAgent.includes("Edge")) browser = "Edge";
  else if (userAgent.includes("Opera")) browser = "Opera";
  
  return browser;
};

const getDeviceInfo = () => {
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  return isMobile ? "Mobile" : "Desktop";
};

const getOsInfo = () => {
  const userAgent = navigator.userAgent;
  let os = "Unknown";
  
  if (userAgent.includes("Win")) os = "Windows";
  else if (userAgent.includes("Mac")) os = "MacOS";
  else if (userAgent.includes("Linux")) os = "Linux";
  else if (userAgent.includes("Android")) os = "Android";
  else if (userAgent.includes("iPhone") || userAgent.includes("iPad")) os = "iOS";
  
  return os;
};

const ReportForm = () => {
  const { user } = useAuth(); // Changed to get user instead of token
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  
  // Determine the current page type and item ID from URL
  const getInitialPageInfo = () => {
    // Default values
    let pageInfo = {
      url: window.location.href,
      pageType: 'other',
      itemId: ''
    };
    
    // Try to determine from URL pattern
    const path = location.pathname;
    if (path.includes('/anime/')) {
      pageInfo.pageType = 'anime';
      pageInfo.itemId = path.split('/anime/')[1];
    } else if (path.includes('/manga/')) {
      pageInfo.pageType = 'manga';
      pageInfo.itemId = path.split('/manga/')[1];
    } else if (path.includes('/character/')) {
      pageInfo.pageType = 'character';
      pageInfo.itemId = path.split('/character/')[1];
    } else if (path.includes('/user/')) {
      pageInfo.pageType = 'user';
      pageInfo.itemId = path.split('/user/')[1];
    }
    
    return pageInfo;
  };
  
  // Form state
  const [formData, setFormData] = useState({
    reportType: 'bug',
    title: '',
    description: '',
    stepsToReproduce: '',
    location: getInitialPageInfo(),
    environment: {
      browser: getBrowserInfo(),
      device: getDeviceInfo(),
      operatingSystem: getOsInfo()
    },
    correctInformation: ''
  });
  
  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Handle nested objects in form state
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  // Handle file attachments
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    
    // Limit to 5 files
    if (attachments.length + files.length > 5) {
      setError('You can only upload up to 5 files');
      return;
    }
    
    // Check file size (limit to 5MB per file)
    const oversizedFiles = files.filter(file => file.size > 5 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      setError('Files must be less than 5MB each');
      return;
    }
    
    // Add new files
    setAttachments(prev => [...prev, ...files]);
    
    // Create preview URLs for images
    const newPreviewUrls = files
      .filter(file => file.type.startsWith('image/'))
      .map(file => URL.createObjectURL(file));
    
    setPreviewUrls(prev => [...prev, ...newPreviewUrls]);
  };
  
  // Remove an attachment
  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
    
    // Revoke the URL to prevent memory leaks
    if (previewUrls[index]) {
      URL.revokeObjectURL(previewUrls[index]);
    }
    setPreviewUrls(prev => prev.filter((_, i) => i !== index));
  };
  
  // Clean up preview URLs when component unmounts
  useEffect(() => {
    return () => {
      previewUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [previewUrls]);
  
  // Submit the form
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setLoading(true);
    
    try {
      // Validate required fields
      if (!formData.title.trim()) {
        throw new Error('Please enter a title for your report');
      }
      
      if (!formData.description.trim()) {
        throw new Error('Please describe the issue you are reporting');
      }
      
      // Get token from localStorage for API call
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication error. Please log in again.');
      }
      
      // Submit report
      await submitReport(formData, attachments, token);
      
      
      // Handle success
      setLoading(false);
      setSuccess(true);
      
      // Reset form
      setFormData({
        reportType: 'bug',
        title: '',
        description: '',
        stepsToReproduce: '',
        location: getInitialPageInfo(),
        environment: {
          browser: getBrowserInfo(),
          device: getDeviceInfo(),
          operatingSystem: getOsInfo()
        },
        correctInformation: ''
      });
      setAttachments([]);
      setPreviewUrls([]);
      
      // Scroll to top
      window.scrollTo(0, 0);
    } catch (err) {
      setLoading(false);
      setError(
        err.response?.data?.error || 
        err.message || 
        'Failed to submit report. Please try again.'
      );
    }
  };
  
  if (success) {
    return (
      <div className="report-success">
        <div className="success-icon">
          <i className="fa-solid fa-check"></i>
        </div>
        <h2>Report Submitted Successfully!</h2>
        <p>Thank you for your feedback. Our team will review your report and take appropriate action.</p>
        <p>You will receive a confirmation email shortly, and you can track the status of your report in the "My Reports" section.</p>
        <button 
          className="new-report-btn" 
          onClick={() => setSuccess(false)}
        >
          Submit Another Report
        </button>
      </div>
    );
  }
  
  return (
    <div className="report-form-container">
      <h2>Submit a Report</h2>
      <p className="form-description">
        Help us improve by reporting bugs, incorrect information, or suggesting new features. Please provide as much detail as possible.
      </p>
      
      {error && (
        <div className="error-message">
          <i className="fa-solid fa-circle-exclamation"></i>
          <p>{error}</p>
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="reportType">Report Type</label>
          <select 
            id="reportType" 
            name="reportType" 
            value={formData.reportType} 
            onChange={handleChange}
            required
          >
            <option value="bug">Bug/Technical Issue</option>
            <option value="incorrect_info">Incorrect Information</option>
            <option value="missing_content">Missing Content</option>
            <option value="feature_request">Feature Request</option>
            <option value="other">Other</option>
          </select>
        </div>
        
        <div className="form-group">
          <label htmlFor="title">Title</label>
          <input 
            type="text" 
            id="title" 
            name="title" 
            value={formData.title} 
            onChange={handleChange}
            placeholder="Brief summary of the issue"
            required
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="description">Description</label>
          <textarea 
            id="description" 
            name="description" 
            value={formData.description} 
            onChange={handleChange}
            placeholder="Please provide a detailed description of the issue"
            rows="5"
            required
          />
        </div>
        
        {formData.reportType === 'bug' && (
          <div className="form-group">
            <label htmlFor="stepsToReproduce">Steps to Reproduce</label>
            <textarea 
              id="stepsToReproduce" 
              name="stepsToReproduce" 
              value={formData.stepsToReproduce} 
              onChange={handleChange}
              placeholder="1. Go to...\n2. Click on...\n3. Notice that..."
              rows="4"
            />
          </div>
        )}
        
        {(formData.reportType === 'incorrect_info' || formData.reportType === 'missing_content') && (
          <div className="form-group">
            <label htmlFor="correctInformation">Correct Information</label>
            <textarea 
              id="correctInformation" 
              name="correctInformation" 
              value={formData.correctInformation} 
              onChange={handleChange}
              placeholder="Please provide the correct information or what content is missing"
              rows="4"
            />
          </div>
        )}
        
        <div className="form-group">
          <label htmlFor="location.url">URL (Where did you find this issue?)</label>
          <input 
            type="text" 
            id="location.url" 
            name="location.url" 
            value={formData.location.url} 
            onChange={handleChange}
          />
        </div>
        
        <div className="form-group file-upload">
          <label>
            Attachments ({attachments.length}/5)
            <input 
              type="file" 
              onChange={handleFileChange} 
              multiple 
              accept="image/*,.pdf,.doc,.docx,.txt" 
              disabled={attachments.length >= 5}
            />
          </label>
          <p className="file-help">Upload screenshots or relevant files (max 5 files, 5MB each)</p>
          
          {attachments.length > 0 && (
            <div className="attachment-previews">
              {attachments.map((file, index) => (
                <div key={index} className="attachment-item">
                  {file.type.startsWith('image/') && previewUrls[index] ? (
                    <img src={previewUrls[index]} alt={`Preview ${index}`} className="attachment-preview" />
                  ) : (
                    <div className="file-icon">{file.name.split('.').pop()}</div>
                  )}
                  <div className="attachment-info">
                    <span>{file.name}</span>
                    <button 
                      type="button" 
                      onClick={() => removeAttachment(index)}
                      className="remove-attachment"
                    >
                      <i className="fa-solid fa-xmark"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        <div className="form-group environment-info">
          <h3>System Information</h3>
          <div className="environment-details">
            <div className="env-item">
              <i className="fa-solid fa-globe"></i>
              <span>Browser:</span>
              <strong>{formData.environment.browser}</strong>
            </div>
            <div className="env-item">
              <i className="fa-solid fa-mobile-screen"></i>
              <span>Device:</span>
              <strong>{formData.environment.device}</strong>
            </div>
            <div className="env-item">
              <i className="fa-solid fa-desktop"></i>
              <span>OS:</span>
              <strong>{formData.environment.operatingSystem}</strong>
            </div>
          </div>
        </div>
        
        <div className="form-actions">
          <button 
            type="submit" 
            className="submit-btn" 
            disabled={loading}
          >
            {loading ? (
              <>
                <i className="fa-solid fa-spinner fa-spin"></i>
                Submitting...
              </>
            ) : (
              <>
                <i className="fa-solid fa-paper-plane"></i>
                Submit Report
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ReportForm;